import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ICompanyInfo, IGroupCompanyInfo } from "../../services/types/LeadRequisitionType";
import { getSearchCompanyData } from "../../services/api/apiService";
import FooterButtonHoc from "../../common/FooterButtonHoc";
import { useData } from "../../services/context/DataContext";
import { COMPANY_FETCH_URL } from "../../services/api/apiUrls";
import { Typeahead, Search } from "@citi-icg-172888/icgds-react";
import SelectHoc from "../../common/SelectHoc";
import _, { isEmpty } from "lodash";
import BusinessCard from "./BusinessCard";
import SalesSize from "./SalesSize";
import { validateSaleSize, validateInputField } from "../../services/utils/utils";

import { COMPANY_SEARCH_COUNT, DEFAULT_LIMIT_COUNT } from "../../constants/textConstants";
import { convertComapaniesArrayData } from "../../utils/companyUtils";
import { getNextSteps } from "../../services/utils/utils";
import CustomAlert from "./CustomAlert";
import { handleScript } from "../../services/utils/utils";

interface SearchCompanyProps {
  setIsAddCompanyInfo: (isAddCompanyInfo: boolean) => void;
}

const CompanySearch: React.FC<SearchCompanyProps> = ({ setIsAddCompanyInfo }) => {
  const {
    companyInfo,
    setCompanyInfo,
    isAboutBusiness,
    setIsAboutBusiness,
    staticData,
    steps,
    setSteps,
    isEditMode,
    setIsCancelClicked,
  } = useData();
  const [value, setValue] = useState<any[] | never[]>([]);
  const countries = _.keys(staticData.countries);
  const [options, setOptions] = useState<string[]>([]);
  const [searchBusiness, setSearchBusiness] = useState<boolean>(false);
  const [filteredCompanyData, setFilteredCompanyData] = useState<ICompanyInfo[]>([]);
  const [showSearchView, setShowSearchView] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Partial<Record<string, boolean>>>({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectRef.current?.focus();
    if (window.innerWidth < 768 && window.parent.location.href.includes("contact-us")) {
      window.parent.scroll(0, 0);
    } else if (window.parent.location.href.includes("contact-us")) {
      window.parent.scroll({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const getCompaniesData = async () => {
      try {
        if (searchText.length > 2) {
          const limit = showSearchView ? DEFAULT_LIMIT_COUNT : COMPANY_SEARCH_COUNT;
          const searchedCompanies = showSearchView ? filteredCompanyData : [];
          const body = {
            global_ultimate_country_name:
              companyInfo?.global_ultimate_country_name === "United States"
                ? "USA"
                : companyInfo?.global_ultimate_country_name?.toUpperCase(),
            searchText,
            limit,
            offset: 0,
          };
          const data = await getSearchCompanyData(COMPANY_FETCH_URL, body);
          const companiesDetails: ICompanyInfo[] = convertComapaniesArrayData(data, staticData);
          const allCompanies = _.concat(searchedCompanies, companiesDetails);
          setFilteredCompanyData(allCompanies);
        } else {
          setFilteredCompanyData([]);
          setOptions([]);
        }
      } catch (error) {
        onGroupDataChange({});
        console.error("🚀 ~ getCompaniesData ~ error:", error);
      }
    };
    getCompaniesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, showSearchView]);

  const onChangeHandler = (event: any) => {
    const sanitizedInput = event.replace(/[^a-zA-Z0-9\s]/g, "");
    if(event !== sanitizedInput){
      setErrors(prevErros=>({...prevErros,searchText:"Special Character Not allowed"}));
    }else{
      setErrors(prevErros=>({...prevErros,searchText:""}));
      setSearchText(sanitizedInput);
    }
    setSearchText(event);
};


  useEffect(() => {
    if (companyInfo?.legalNameOfBusiness) {
      setSearchBusiness(true);
    }
    if (searchBusiness && cardRef.current) {
      cardRef.current.focus();
    }
  }, [searchBusiness]);

  const onClickCloseSearchHandler = useCallback(() => {
    setCompanyInfo(null);
    setIsAboutBusiness(false);
    setSearchText("");
    setShowSearchView(false);
    setIsCancelClicked(true);
  }, [setCompanyInfo]);

  const onClickBlackHandler = useCallback(() => {
    const updatedCompanyInfo: any = {
      businessInfo: companyInfo?.businessInfo,
    };
    setCompanyInfo(updatedCompanyInfo);
    setIsAboutBusiness(false);
    setSearchText("");
    setShowSearchView(false);
  }, [setCompanyInfo]);

  const onChangeDDHandler = useCallback(
    (options: string[], ddName: string) => {
      const updatedCompanyInfo: any = {
        ...companyInfo,
        [ddName]: options,
      };

      if (ddName === "global_ultimate_country_name") {
        const updatedCompanyInfo: any = {
          global_ultimate_country_name: options,
          businessInfo: companyInfo?.businessInfo,
        };
        if (!options.includes("United States")) {
          setIsAddCompanyInfo(true);
        }
        setSearchBusiness(false);
        setCompanyInfo(updatedCompanyInfo);
      } else {
        setCompanyInfo(updatedCompanyInfo);
      }

      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, [ddName]: true };
        validateFields(updatedCompanyInfo, newTouchedFields);
        return newTouchedFields;
      });
    },
    [companyInfo, setCompanyInfo]
  );

  const onChangeBusinessHandler = () => {
    if (companyInfo?.global_ultimate_country_name) {
      setIsAddCompanyInfo(true);
      const updatedCompanyInfo: any = {
        ...companyInfo,
        ["isCompanySource_Flag"]: false,
      };
      setCompanyInfo(updatedCompanyInfo);
    }
  };

  const renderLabel = (item: any) => {
    if (isEmpty(item) && searchText.length > 2) {
      return (
        <div id="no_record" tabIndex={0}>
          {"No record found"}
        </div>
      );
    } else if (!isEmpty(item)) {
      return (
        <div
          id="legal_business_id"
          tabIndex={0}
          style={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          <div className="lmn-font-size-lg lmn-font-weight-bold">{item.legalNameOfBusiness}</div>
          <div className="lmn-font-size-xs " style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
            <span className="lmn-text-color-desc lmn-pr-3 lmn-border-color-1">{item.address}</span>
          </div>
        </div>
      );
    }
  };

  const onGroupDataChange = useCallback(
    (item: any) => {
      if (!isEmpty(item)) {
        const renderOptions = Object.keys(item).map((country) =>
          item[country].map((cData: ICompanyInfo) => {
            return {
              label: renderLabel(cData),
              value: JSON.stringify(cData),
            };
          })
        );
        setOptions(() => [...(renderOptions?.[0] ?? [])]);
      } else {
        const renderOptions: any = {
          label: renderLabel(item),
          value: "",
        };
        setOptions(() => {
          return [renderOptions];
        });
      }
    },
    [setOptions, renderLabel]
  );

  /// Group by Country Data
  const groupByCompanyData: IGroupCompanyInfo = useMemo(
    () => _.groupBy(filteredCompanyData, "headquaterCountry"),
    [filteredCompanyData]
  );
  useEffect(() => {
    onGroupDataChange(groupByCompanyData);
  }, [filteredCompanyData]);

  const validateFields = (
    updatedCompanyData: ICompanyInfo | null,
    updatedTouchedFields = touchedFields
  ) => {
    const newErrors: Partial<Record<string, string>> = { ...errors };

    if (
      updatedTouchedFields.global_ultimate_country_name &&
      !validateInputField(updatedCompanyData?.global_ultimate_country_name)
    ) {
      newErrors.global_ultimate_country_name = "Please select global ultimate country name";
    } else delete newErrors.global_ultimate_country_name;

    const lowerCaseUrl = updatedCompanyData?.website?.toLowerCase() ?? "";
    if (updatedTouchedFields.website && !validateInputField(lowerCaseUrl, "website")) {
      newErrors.website = "Please enter a valid Comapany Website";
    } else delete newErrors.website;

    if (updatedTouchedFields.industry && !validateInputField(updatedCompanyData?.industry)) {
      newErrors.industry = "Please select industry type";
    } else delete newErrors.industry;

    if (updatedTouchedFields.salesSize && !validateInputField(updatedCompanyData?.salesSize)) {
      newErrors.salesSize = "Please select annual revenue";
    } else delete newErrors.salesSize;

    if (
      updatedTouchedFields.isCompanyVCorPEBacked &&
      !validateInputField(updatedCompanyData?.isCompanyVCorPEBacked)
    ) {
      newErrors.isCompanyVCorPEBacked = "Please select is your Company VC or PE Backed";
    } else delete newErrors.isCompanyVCorPEBacked;

    const isValuePresent = (options as unknown as { value: string }[]).some(
      (option) => typeof option === "object" && option.value !== ""
    );
    if (updatedTouchedFields.registerBusiness && !isValuePresent && searchText.length < 2) {
      newErrors.registerBusiness = "Please select is your Registered Company Name";
    } else delete newErrors.registerBusiness;

    setErrors(newErrors);
    return _.isEmpty(newErrors);
  };

  const handleChange = (e: any) => {
    const companyData: any = JSON.parse(e);
    const updateCompanyInfo = {
      businessInfo: companyInfo?.businessInfo,
      ["isCompanySource_Flag"]: true,
      ...companyData,
    } as ICompanyInfo;
    setCompanyInfo(updateCompanyInfo);
    setSearchBusiness(true);
  };

  const onSubmitHandler = () => {
    setIsSubmitClicked(true);
    if (enabledSubmit) {
      const updatedSteps = !countryValidation(companyInfo?.global_ultimate_country_name)
        ? getNextSteps(steps, 0, isEditMode)
        : getNextSteps(steps, 1, isEditMode);
      if (!countryValidation(companyInfo?.global_ultimate_country_name)) {
        setIsAddCompanyInfo(true);
      }
      handleScript(divRef, "ccbContactUsFormStep1CompanyInfoCompleted");
      setSteps(updatedSteps);
      setErrors({});
    } else {
      setTouchedFields(() => {
        let newTouchedFields = {};
        const isValuePresent = (options as unknown as { value: string }[]).some(
          (option) => typeof option === "object" && option.value !== ""
        );
        if (!countryValidation(companyInfo?.global_ultimate_country_name)) {
          newTouchedFields = { global_ultimate_country_name: true };
        } else if (!isValuePresent) {
          newTouchedFields = {
            registerBusiness: true,
          };
        } else {
          newTouchedFields = {
            industry: true,
            salesSize: true,
            isCompanyVCorPEBacked: false,
            website: true,
          };
          if (companyInfo?.salesSize) {
            newTouchedFields = { ...newTouchedFields, isCompanyVCorPEBacked: true };
          }
        }
        validateFields(companyInfo, newTouchedFields);
        return newTouchedFields;
      });
    }
    if (window.parent.location.href.includes("contact-us")) window.scrollTo(0, 0);
  };

  const handleTypeaheadChanges = () => {
    if (searchBusiness) {
      return (
        <BusinessCard
          id="typeahead_business_card"
          companyData={companyInfo}
          setSearchBusiness={setSearchBusiness}
          setValue={setValue}
          ref={cardRef}
        />
      );
    } else {
      const isValuePresent = (options as unknown as { value: string }[]).some(
        (option) => typeof option === "object" && option.value !== ""
      );

    return (
        <div className="searchWrap lmn-mb-4" id="typeahead_business">
          <Typeahead
            value={value}
            id="registered_company_name"
            onSearch={(e: any) => onChangeHandler(e)}
            onChange={(e: any) => handleChange(e)}
            options={options}
            // aria-labelledby="Business Name"
            allowClear
            invalid={!!errors.registerBusiness || !!errors.searchText} // Fix: Convert string to boolean
            highlightOption
            popupStyle={{ display: searchText.length > 2 && isValuePresent ? "" : "none" }}
            aria-required={true}
            aria-describedby={"registered_business_name searchText" 
            }
          >
            <Search size="md" placeholder="Type here" />
          </Typeahead>
          {/* {errors.registerBusiness && <div aria-live="assertive" role="alert" id="registered_business_name"><small>{errors.registerBusiness}</small></div>} */}
          {errors.registerBusiness && (
            <div aria-live="assertive" role="alert" id="registered_business_name">
              <small>{errors.registerBusiness}</small>
            </div>
          )}
          {errors.searchText && (
            <div aria-live="assertive" role="alert" id="search_text_error">
              <small>{errors.searchText}</small>
            </div>
          )}
        </div>
      );
    }
  };

  const countryValidation = (item: any) => {
    const countryName = Array.isArray(item) ? item[0]?.toLowerCase() : item?.toLowerCase();
    return countryName && (countryName === "united states" || countryName === "usa");
  };

  const enabledSubmit = useMemo(() => {
    if (!countryValidation(companyInfo?.global_ultimate_country_name)) {
      return companyInfo?.global_ultimate_country_name;
    } else {
      return (
        companyInfo?.global_ultimate_country_name &&
        companyInfo?.industry &&
        companyInfo?.salesSize &&
        validateInputField(companyInfo?.website?.toLocaleLowerCase(), "website") &&
        validateSaleSize(companyInfo)
      );
    }
  }, [companyInfo]);

  const handleAlert = () => {
    setIsSubmitClicked(!isSubmitClicked);
  };

  return (
    <>
      <div>
        <div ref={divRef}></div>
        <div className="lmn-dnca-w-l-100 lmn-dnca-w-m-100 lmn-dnca-w-s-100">
          {isSubmitClicked && Object.values(errors).length > 0 && (
            <CustomAlert
              id="companySearch-alert"
              errors={Object.values(errors)}
              handleChange={handleAlert}
              isSubmitClicked={isSubmitClicked}
            />
          )}
        </div>
      </div>
      <div
        className={`lmn-dnca-w-l-50 lmn-dnca-w-m-50 lmn-dnca-w-s-100 lmn-mb-6 ${!countryValidation(companyInfo?.global_ultimate_country_name) && "lmn-mb-64px"}`}
      >
        <label htmlFor="headquarter location" className="label lmn-required" id="business_name">
          Headquarters Location
        </label>
        <SelectHoc
          // ref={selectRef}
          id="global_country"
          label={"Headquarters Location"}
          placeholder="Select Country"
          options={countries ?? []}
          value={companyInfo?.global_ultimate_country_name ?? ""}
          error={errors.global_ultimate_country_name}
          onChange={(options: any) => onChangeDDHandler(options, "global_ultimate_country_name")}
        />
      </div>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {countryValidation(companyInfo?.global_ultimate_country_name) && (
          <>
            <label className="label lmn-mb-3 lmn-required" htmlFor="registered_company_name" >
              Registered Company Name
            </label>
            {handleTypeaheadChanges()}
          </>
        )}
        {searchBusiness && countryValidation(companyInfo?.global_ultimate_country_name) ? (
          <>
            <SalesSize
              errorIndustryType={errors.industry}
              errorSalesSize={errors.salesSize}
              errorVcPe={errors.isCompanyVCorPEBacked}
              errorWebsite={errors.website}
              validateFields={validateFields}
              setTouchedFields={setTouchedFields}
              tochedFields={touchedFields}
            />
          </>
        ) : (
          countryValidation(companyInfo?.global_ultimate_country_name) && (
            <>
              <label className="lmn-mb-3 lmn-mt-6 lmn-font-size-12">
                If your company name is not appearing here, please add your company information
                manually
              </label>
              <div>
                <FooterButtonHoc
                  showDivider={false}
                  cancelText="Add Company"
                  onCancel={onChangeBusinessHandler}
                />
              </div>
            </>
          )
        )}
        {isAboutBusiness && (
          <>
            <div>
              <FooterButtonHoc
                submitText="Continue"
                onBack={onClickBlackHandler}
                onCancel={onClickCloseSearchHandler}
                onSubmit={onSubmitHandler}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default React.memo(CompanySearch);
